import StatefulSettings from "../controllers/StatefulSettings";
import {
  ITaskTimerInfo,
  TaskBasketItem,
  ITaskItem,
  ITaskBasketInfo,
  ISettingsItem,
  TaskItem,
  ITaskProofRequestItem,
  IPenaltyProofRequestItem,
  PenaltyBasketItem,
  PenaltyItem,
  AuditMessage,
  IShowNotificationState,
  ITypingStats,
  IWeeklyTargetProgress,
} from "../ts/Definitions";
import {
  AuditActionTypes,
  ItemTypes,
  TaskType,
  TraceLevel,
  ProofStatus,
} from "../ts/enums";
import {
  isTimedItem,
  getRandomItem,
  getScheduleReason,
  flipCoin,
} from "../utilities/utils";
import {
  sendRequestProof,
  NotificationType,
} from "../controllers/messaging";
import { FEATURE_FLAG_TRACE_LOGS } from "../utilities/ConfigCache";
import { STATE_KEY_PROOF_REQUESTS } from "../controllers/StatefulSettings";
import { handleDiscordRequestProofTicker } from "../utilities/discord";
import UIFx from "uifx";
import ringSound from "../assets/ring.wav";
import Emitter from "../utilities/Emitter";

const getWeekKey = (date: Date): string => {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-W${weekNo.toString().padStart(2, "0")}`;
};

const ring = new UIFx(ringSound);

/**
 * TaskManager - Handles all task-related operations including:
 * - Task lifecycle (completion, timers, scheduling)
 * - Task validation and proof requests
 * - Permanent tasks management
 * - Task basket operations
 * - Credit/reward calculations
 */
export class TaskManager {
  private settings!: StatefulSettings;
  private showMessage!: (args: IShowNotificationState) => void;

  /**
   * Initialize the TaskManager with required dependencies
   */
  public initialize(
    settings: StatefulSettings,
    showMessage: (args: IShowNotificationState) => void
  ): void {
    this.settings = settings;
    this.showMessage = showMessage;
  }

  /**
   * Refreshes all active tasks, checking for completion and updating timers
   */
  public refreshTasks(): void {
    const now = new Date();
    const taskSchedule = this.settings.getTaskSchedule();
    const keys = Object.keys(taskSchedule);

    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      if (
        taskSchedule[key].isPaused ||
        taskSchedule[key].isComplete ||
        (taskSchedule[key] as ITaskTimerInfo).permanent
      )
        continue;

      const endTime = taskSchedule[key].endTime || now;
      const remainingMs = endTime.getTime() - now.getTime();
      const newTicks = Math.ceil(remainingMs / 60000);
      const eventScheduled = this.settings.checkSettingItemEventTriggerValid(
        taskSchedule[key]
      );

      if (!eventScheduled && (!Number.isFinite(remainingMs) || remainingMs <= 0)) {
        const monitoredTimer = (taskSchedule[key] as ITaskTimerInfo).monitoredTimer === true;
        this.onTaskCompleted(key);
        // special handling to play sound when countdown task has completed.
        if (this.settings.getIsSoundEnabled() && !monitoredTimer) ring.play();
        Emitter.emitNotification({
          message: `Timed Task ${taskSchedule[key].name} Complete`,
          success: true,
          suppressSound: monitoredTimer,
          notificationType: NotificationType.ScheduleInfo,
        });
      } else {
        taskSchedule[key].timerTicks = newTicks < 0 ? 0 : newTicks;
        try {
          this.settings.updateTaskState(taskSchedule[key]);
        } catch (error: unknown) {
          console.error("Failed to update task state:", error);
        }
      }
    }
  }

  /**
   * Marks a task as completed, handles rewards, and manages multi-run tasks
   */
  public onTaskCompleted(key: string, notifyUI = true): number {
    const enableDetailLogging = this.settings
      .getConfig()
      ?.allowedFeature(
        FEATURE_FLAG_TRACE_LOGS,
        this.settings.getControl().username,
        false
      );
    const taskInfo = this.settings.getTaskSchedule();
    let reward = 0;

    if (!taskInfo[key]) {
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Task,
          key,
          `Unable to complete task ${key} as not in schedule`,
          TraceLevel.warning
        )
      );
      return reward;
    }

    taskInfo[key].isComplete = true;
    taskInfo[key].completedTime = new Date();
    taskInfo[key].isPaused = false;
    delete taskInfo[key].timerTicks;

    const task = this.settings.getTask(key) || taskInfo[key];
    if (task) {
      if ((task as ITaskTimerInfo).credit) {
        reward = this.settings.getReward(
          Math.abs((task as ITaskTimerInfo).credit)
        );
        const newBank = this.settings.incrementBank(reward, task.name);
        this.auditIncrement(task.name, "Timed Task - Complete", newBank);
      } else {
        this.settings.auditUser(
          new AuditMessage(
            this.getAuditActionType(task.itemType),
            task.name,
            "Complete",
            TraceLevel.info,
            true
          )
        );
      }
    }

    const basketItem = taskInfo[key] as ITaskTimerInfo;
    if (
      basketItem.creditTaskType === TaskType.roulette &&
      basketItem.timesRequired &&
      basketItem.timesRequired > 1
    ) {
      const hasMoreRuns = typeof (basketItem as TaskBasketItem).decrementTimesRemaining === "function"
        ? (basketItem as TaskBasketItem).decrementTimesRemaining()
        : ((basketItem.timesRequired = Math.max(0, (basketItem.timesRequired ?? 1) - 1)) > 0);
      this.settings.auditUser(
        new AuditMessage(
          this.getAuditActionType(task.itemType),
          task.name,
          `Resetting task for further task required ${basketItem.timesRequired} times`,
          TraceLevel.info,
          true
        )
      );
      if (hasMoreRuns) {
        // reset the task to run again
        taskInfo[key].isComplete = false;
        taskInfo[key].isPaused = true;
        taskInfo[key].timerTicks = taskInfo[key].expectedCount;
        this.settings.updateTaskState(taskInfo[key]);
        return 0; // no reward if required multiple times
      }
    }

    this.settings.updateTaskState(taskInfo[key]);

    if (
      basketItem.itemType === ItemTypes.task &&
      TaskItem.IsWeeklyNumericTargetTask(basketItem)
    ) {
      const completionTime = basketItem.completedTime || new Date();
      const startedAt = basketItem.startedTime || completionTime;
      const weekKey = getWeekKey(completionTime);
      const targetValue = basketItem.weeklyTargetValue ?? basketItem.expectedCount ?? 0;
      // taskSchedule entries can be plain persisted objects without basket methods.
      // Wrap into a TaskBasketItem before requesting proof so getBasketText exists.
      const proofBasketItem = new TaskBasketItem(basketItem as ITaskTimerInfo);
      const proofCode = this.requestProof(proofBasketItem);
      const progress: IWeeklyTargetProgress = {
        taskKey: basketItem.key,
        weekKey,
        startedAt: startedAt.toISOString(),
        dueAt: completionTime.toISOString(),
        targetValue,
        proofCode,
        penaltyKey: basketItem.weeklyTargetPenaltyKey,
      };
      void this.settings.saveWeeklyTargetProgress(progress);
    }

    if (notifyUI) this.settings.fireListeners();
    return reward;
  }

  /**
   * Handles proof request logic for tasks
   */
  public handleProofRequest(item: TaskBasketItem | PenaltyBasketItem): void {
    if (item.proofStatus === ProofStatus.neverRequestProof) return;

    // dont even bother if not a standard task
    if (
      (item.itemType === ItemTypes.task &&
        !TaskItem.IsStandardTask(item as ITaskItem)
        && (item as ITaskItem).creditTaskType !== TaskType.acknowledgement) ||
      (item.itemType === ItemTypes.penalty &&
        !PenaltyItem.IsPenaltyTask(item as any))
    )
      return;

    if (
      (item.itemType === ItemTypes.event ||
        item.itemType === ItemTypes.permission) &&
      !item.selectedCondition && item
    ) {
      // this means we are an event or permission but they can't directly provide proof if a custom event
      return;
    }

    const proofRequestsSentToday = this.settings.getState(
      STATE_KEY_PROOF_REQUESTS,
      0
    );
    const { verificationLimit } = this.settings.getSessionSettings();
    const requestProof =
      item.proofStatus === ProofStatus.alwaysRequestProof ||
      (item.proofStatus === ProofStatus.random &&
        this.settings.isLoaded() &&
        this.settings.getSessionSettings().randomVerifications === true &&
        (!verificationLimit || verificationLimit > proofRequestsSentToday) &&
        flipCoin() &&
        flipCoin());

    if (requestProof) {
      this.requestProof(item);
    }
  }

  /**
   * Requests proof for a task item
   */
  private requestProof(item: TaskBasketItem | PenaltyBasketItem): string {
    const ignoreDialog = !this.settings.canHandleDiscordProofCalls();
    const proofText = sendRequestProof(
      item,
      this.settings,
      undefined,
      ignoreDialog === false
    );
    const proofItem = item as ITaskProofRequestItem | IPenaltyProofRequestItem;
    proofItem.proofCode = proofText;
    this.settings.updateProofDialog(proofItem, true);
    const proofRequestsSentToday = this.settings.getState(
      STATE_KEY_PROOF_REQUESTS,
      0
    );
    this.settings.updateProofRequests(proofRequestsSentToday + 1);
    handleDiscordRequestProofTicker(this.settings, proofText);
    return proofText;
  }

  /**
   * Adds a permanent task to the schedule
   */
  public addPermanentTask(
    itemToAdd: TaskItem,
    taskSchedule: ITaskBasketInfo
  ): void {
    if (taskSchedule[itemToAdd.key]) {
      const existingItem = taskSchedule[itemToAdd.key] as ITaskTimerInfo;
      if (existingItem.permanent) return;
      existingItem.permanent = true;
      this.settings.updateTaskState(existingItem);
      this.showMessage({
        message: "Task made permanent",
        notificationType: NotificationType.ScheduleInfo,
      });
      return;
    }

    if (!this.settings.canAddTask(itemToAdd).isOk) {
      const validation = this.settings.canAddTask(itemToAdd);
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Task,
          itemToAdd.name,
          `Cannot add permanent task: ${validation.reason || 'Unknown reason'}`,
          TraceLevel.warning
        )
      );
      return;
    }

    const timerInfo: ITaskTimerInfo = {
      ...itemToAdd,
      permanent: true,
      isPenalty: false,
      selectedCondition: getRandomItem(itemToAdd.options || []),
      isPaused: false,
      isComplete: false,
      scheduleReason: getScheduleReason(itemToAdd, { isPermanent: true }),
    };

    const items = this.settings.selectItemsForTask(
      itemToAdd,
      undefined,
      false
    );
    if (itemToAdd.isItem) {
      if (items) timerInfo.selectedCondition = items[itemToAdd.key];
    } else timerInfo.selectedItems = items;

    const creditItem = new TaskBasketItem(timerInfo);
    taskSchedule[itemToAdd.key] = creditItem;

    const randomCount = this.settings.getTaskTimerTicks(
      itemToAdd,
      this.settings.getIsWeekend
    );
    if (!isTimedItem(itemToAdd.countType) && !isNaN(randomCount)) {
      taskSchedule[itemToAdd.key].expectedCount = randomCount;
    }

    this.settings.updateTaskState(taskSchedule[itemToAdd.key]);
    this.showMessage({
      message: "New Task Available",
      notificationType: NotificationType.ScheduleInfo,
    });
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Task,
        creditItem.name,
        "Permanent Applied",
        TraceLevel.info
      )
    );
  }

  /**
   * Resets a task to "saved for later" status
   */
  public handleCreditItemReset(item: ISettingsItem): void {
    const creditInfo = this.settings.getTaskSchedule();
    const timer = creditInfo[item.key];
    timer.isPaused = true;
    delete timer.endTime;
    this.settings.updateTaskState(timer);
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Task,
        item.name,
        "Reset to saved for later",
        TraceLevel.info
      )
    );
  }

  /**
   * Handles lines task completion
   */
  public sendLines(
    lines: string,
    line: string,
    expectedCount: number,
    mistakes: number,
    taskKey: string,
    allowRedoLines: boolean,
    stats: ITypingStats,
    sendLinesToOwner: (lines: string, line: string, expectedCount: number, mistakes: number, taskKey: string, settings: StatefulSettings, allowRedoLines: boolean, stats: ITypingStats) => void
  ): void {
    this.settings.updateLinesDialog(false);

    const basketItem = this.settings
      .getBasketItems(true)
      .pendingItems.find((i) => i.key === taskKey);
    if (!basketItem) {
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Task,
          taskKey,
          "Lines task not found in schedule",
          TraceLevel.warning
        )
      );
      return;
    }

    this.onTaskCompleted(basketItem.key);
    sendLinesToOwner(
      lines,
      line,
      expectedCount,
      mistakes,
      taskKey,
      this.settings,
      allowRedoLines,
      stats
    );
  }

  // Helper methods
  private auditIncrement(name: string, action: string, credit: number): void {
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Task,
        name,
        action,
        TraceLevel.info,
        undefined,
        Math.abs(credit)
      )
    );
  }

  private getAuditActionType(type: ItemTypes): AuditActionTypes {
    switch (type) {
      case ItemTypes.task:
        return AuditActionTypes.Task;
      case ItemTypes.penalty:
        return AuditActionTypes.Penalty;
      case ItemTypes.event:
        return AuditActionTypes.Event;
      case ItemTypes.permission:
        return AuditActionTypes.Permission;
      case ItemTypes.position:
        return AuditActionTypes.Position;
    }
    return AuditActionTypes.Session;
  }
}
